/**
 * Which connected app a skill came from.
 *
 * A skill the genome installed alongside a connector lives in that connector's folder —
 * `connector/gmail/gmail.md` — and the BFF sends that whole key as the skill's `filename`
 * (`humanizeSkillFilename` only drops the path for display). So the toolkit slug is already on the wire:
 * nothing new is fetched and no field is added, the path is simply read instead of discarded.
 *
 * Two folder shapes coexist during the genome migration — the old flat `connector/<slug>.md` and the
 * nested `connector/<slug>/<slug>.md` — and both name the slug in the same segment.
 *
 * Shared 1:1 with iOS `SkillConnector.swift` and Android `SkillConnector.kt`; the three test tables are
 * the same cases.
 */

/** The toolkit slug a skill's genome filename belongs to, or null when it belongs to no connector. */
export function skillConnectorSlug(filename: string | undefined | null): string | null {
  const key = filename?.trim();
  if (!key) return null;
  const nested = /^connector\/([^/]+)\//.exec(key);
  if (nested) return nested[1].toLowerCase();
  const flat = /^connector\/([^/]+)\.md$/i.exec(key);
  return flat ? flat[1].toLowerCase() : null;
}

/**
 * The connector to credit on a skill row: the one its folder names, and only while it is actually
 * connected.
 *
 * The line reads "Uses your Gmail" — a statement about the user's own setup — so a disconnected app must
 * not produce one. Returns the matched connector (the row also renders its logo), or null.
 */
export function skillConnector<C extends { toolkit?: string; name?: string; connected?: boolean }>(
  filename: string | undefined | null,
  connectors: readonly C[] | undefined,
): C | null {
  return connectorForSlug(skillConnectorSlug(filename), connectors);
}

/**
 * The same rule for a caller that already HAS the slug rather than a filename.
 *
 * A routine carries `sourceConnectorSlug` on the entity itself (stamped by `EnsurePulseIdentity`), so it
 * needs no path parsing — but it needs the identical "only while connected" gate, because the line it
 * renders is the same claim about the user's setup.
 */
export function connectorForSlug<C extends { toolkit?: string; name?: string; connected?: boolean }>(
  slug: string | undefined | null,
  connectors: readonly C[] | undefined,
): C | null {
  const wanted = slug?.trim().toLowerCase();
  if (!wanted || !connectors) return null;
  return connectors.find((c) => c.connected === true && (c.toolkit ?? '').toLowerCase() === wanted) ?? null;
}

/** The connector's display name for the credit line, falling back to its title-cased slug. */
export function connectorDisplayName(connector: { toolkit?: string; name?: string }): string {
  const name = connector.name?.trim();
  if (name) return name;
  const slug = (connector.toolkit ?? '').trim();
  return slug ? slug.charAt(0).toUpperCase() + slug.slice(1) : '';
}
